import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1/';

const API = axios.create({
  baseURL: API_BASE_URL,
  // No forced Content-Type: axios sends JSON for plain objects and proper
  // multipart/form-data for FormData (file uploads) automatically. Forcing
  // application/json here made axios serialize FormData to JSON, breaking
  // uploads and list fields.
  timeout: 15000,
});

API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * True when the request never got an HTTP response at all — a dropped
 * connection, not a rejection from the server. Timeouts and deliberate
 * cancellations are excluded: neither should be retried silently.
 */
const isDroppedConnection = (error) =>
  !error.response && error.code !== 'ECONNABORTED' && !axios.isCancel(error);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // A dropped connection is usually transient: the dev server sheds the odd
    // concurrent request, and StrictMode fires every fetch twice. Retry a GET
    // once so pages don't report "Failed to load ..." for a request that would
    // have succeeded. GET only — retrying a write could duplicate it.
    if (
      isDroppedConnection(error) &&
      originalRequest &&
      !originalRequest._netRetry &&
      (originalRequest.method || 'get').toLowerCase() === 'get'
    ) {
      originalRequest._netRetry = true;
      return API(originalRequest);
    }

    // A 401 from login/register/refresh means bad credentials, not an expired
    // session — let the caller show the error instead of reloading /login.
    const isAuthEndpoint = /auth\/(login|register|token\/refresh)\/?$/.test(
      originalRequest?.url || ''
    );

    // Token-addressed lab pages (patient portal) have no session: never refresh or log out for them.
    const isLabPublic = (originalRequest?.url || '').startsWith('lab/public/');
    if (error.response?.status === 401 && !originalRequest?._retry && !isAuthEndpoint && !isLabPublic) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return API(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(
          `${API_BASE_URL.replace(/\/$/, '')}/auth/token/refresh/`,
          { refresh: refreshToken }
        );
        const newAccessToken = data.access;
        localStorage.setItem('accessToken', newAccessToken);
        // ROTATE_REFRESH_TOKENS is on, so the server issues a fresh refresh
        // token on every refresh. Keeping it is what carries the 7-day window
        // forward; discarding it measured that window from login instead, and
        // signed people out a week later however actively they used the app.
        // Guarded because a server with rotation off returns no `refresh`, and
        // storing undefined would write the string "undefined" and break the
        // next refresh outright.
        if (data.refresh) {
          localStorage.setItem('refreshToken', data.refresh);
        }
        API.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return API(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default API;
